import { type Component, type VNode, markRaw, reactive } from 'vue'

// import type { Variants } from '@backmarket/design-system/components/Toast/Toast.constant'
import { IconCheckInCircle } from '@backmarket/design-system/icons/IconCheckInCircle'
import { IconCrossInCircle } from '@backmarket/design-system/icons/IconCrossInCircle'
import { IconInfo } from '@backmarket/design-system/icons/IconInfo'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './useTheToast.translations'

interface ToastState {
  // TODO export them in DS and import them here
  // type: Variants
  type: 'error' | 'success' | 'neutral'
  isOpened: boolean
  title: string
  content: string | VNode
  icon?: object
  duration: number
  name: string
  closeAlternativeText: string
  closeCallback?: () => void
}

interface ToastParams {
  title?: string
  content?: string | VNode
  icon?: Component
  duration?: number
  name?: string
  closeAlternativeText?: string
  closeCallback?: () => void
}

const DEFAULT_TOAST_STATE: ToastState = {
  type: 'success',
  title: '',
  content: '',
  duration: 10000,
  name: 'the-toast',
  closeAlternativeText: '',
  isOpened: false,
}

const toastInfo: ToastState = reactive({ ...DEFAULT_TOAST_STATE })

function closeToast() {
  const { isOpened, closeCallback } = toastInfo
  if (!isOpened) {
    return
  }

  Object.assign(toastInfo, DEFAULT_TOAST_STATE)
  if (closeCallback) {
    closeCallback()
  }
}

function openErrorToast(toastParams?: ToastParams) {
  const i18n = useI18n()

  toastInfo.type = 'error'
  toastInfo.title =
    toastParams?.title || i18n(translations.defaultToastErrorTitle)
  toastInfo.content =
    toastParams?.content || i18n(translations.defaultToastErrorContent)
  toastInfo.icon = markRaw(IconCrossInCircle)
  toastInfo.duration = toastParams?.duration || DEFAULT_TOAST_STATE.duration
  toastInfo.name = toastParams?.name || DEFAULT_TOAST_STATE.name
  toastInfo.closeAlternativeText = i18n(translations.closeToast)
  toastInfo.closeCallback = toastParams?.closeCallback
  toastInfo.isOpened = true
}

function openSuccessToast(toastParams?: ToastParams) {
  const i18n = useI18n()

  toastInfo.type = 'success'
  toastInfo.title =
    toastParams?.title || i18n(translations.defaultToastSuccessTitle)
  toastInfo.content =
    toastParams?.content || i18n(translations.defaultToastSuccessContent)
  toastInfo.icon = toastParams?.icon
    ? markRaw(toastParams.icon)
    : markRaw(IconCheckInCircle)
  toastInfo.duration = toastParams?.duration || DEFAULT_TOAST_STATE.duration
  toastInfo.name = toastParams?.name || DEFAULT_TOAST_STATE.name
  toastInfo.closeAlternativeText =
    toastParams?.closeAlternativeText || i18n(translations.closeToast)
  toastInfo.closeCallback = toastParams?.closeCallback
  toastInfo.isOpened = true
}

function openNeutralToast(
  // title and content are required for neutral toast, as there is no default translation
  toastParams: Required<Pick<ToastParams, 'title' | 'content'>> &
    Omit<ToastParams, 'title' | 'content'>,
) {
  const i18n = useI18n()

  toastInfo.type = 'neutral'
  toastInfo.title = toastParams.title
  toastInfo.content = toastParams.content
  toastInfo.icon = toastParams?.icon
    ? markRaw(toastParams.icon)
    : markRaw(IconInfo)
  toastInfo.duration = toastParams?.duration || DEFAULT_TOAST_STATE.duration
  toastInfo.name = toastParams?.name || DEFAULT_TOAST_STATE.name
  toastInfo.closeAlternativeText =
    toastParams?.closeAlternativeText || i18n(translations.closeToast)
  toastInfo.closeCallback = toastParams?.closeCallback
  toastInfo.isOpened = true
}

export function useTheToast() {
  return {
    openSuccessToast,
    openErrorToast,
    openNeutralToast,
    toastInfo,
    closeToast,
    TOAST_CONTENT_TELEPORT: '#toast-content',
  }
}
